import { useState, useMemo, useCallback, useEffect } from 'react'
import Visualization from './Visualization'
import IndicatorDetails from './IndicatorDetails'
import { useSelector } from 'react-redux'
import { clone, getEndpointFromConfig } from '../../common/utils/helper'
import fetchData from '../../service/apiService'
import { endpointLabels } from '../../common/utils/enum'

const CardDetails = ({
  topicObj,
  redirectURL,
  topicRedirectUrl,
  indicatorRedirectUrl,
  environment,
  featuredDatasetsData,
  searchRedirectUrl,
}) => {
  const { endpointList, apiKeyName, apiKeyValue } = useSelector(
    (state) => state.common
  )
  const getTopicDetials = (topicObject) => {
    const indicators = topicObject.subTopics.reduce((acc, obj) => {
      return [...acc, ...obj.indicators]
    }, [])

    topicObject.subTopics[0].indicators = indicators
    return indicators
  }

  const [topicIndicators, setTopicIndicators] = useState(
    getTopicDetials(clone(topicObj))
  )
  const [topicDetails, setTopicDetails] = useState(topicObj)
  const { countryDropdownValue, defaultCountryCode } = useSelector(
    (state) => state.themeProfile
  )

  const initialLoadingStates = useMemo(
    () =>
      topicDetails.subTopics.reduce((acc, obj) => {
        obj.indicators.forEach((indicator) => {
          acc[indicator.id] = false
        })
        return acc
      }, {}),
    [topicDetails.subTopics]
  )

  const [loadingStates, setLoadingStates] = useState(initialLoadingStates)
  const [indicatorCount, setIndicatorCount] = useState('--')
  const [datasetCount, setDatasetCount] = useState('--')

  useEffect(() => {
    getIndicatorAndDatasetCount(topicObj.id)
  }, [topicObj])

  const getIndicatorAndDatasetCount = async (topicId) => {
    try {
      const indicatorRequest = {
        count: true,
        facets: [],
        orderby: '',
        select: '',
        searchFields: '',
        search: ``,
        top: 0,
        skip: 0,
        filter: `type eq 'indicator' and series_description/topics/any(t3: (t3/id eq '${topicId}')) and (series_description/topics/any(t3: (t3/vocabulary eq 'WB Practice Groups')) or series_description/topics/any(t3: (t3/vocabulary eq 'Data360 Topic L1')) or series_description/topics/any(t3: (t3/vocabulary eq 'Data360 Topic L2')))`,
      }

      const datasetRequest = {
        count: true,
        facets: [],
        orderby: '',
        select: '',
        searchFields: '',
        search: ``,
        top: 0,
        skip: 0,
        filter: `type eq 'dataset' and series_description/topics/any(t3: (t3/id eq '${topicId}')) and (series_description/topics/any(t3: (t3/vocabulary eq 'WB Practice Groups')) or series_description/topics/any(t3: (t3/vocabulary eq 'Data360 Topic L1')) or series_description/topics/any(t3: (t3/vocabulary eq 'Data360 Topic L2')))`,
      }
      const headers = apiKeyName
        ? {
            [apiKeyName]: apiKeyValue,
          }
        : undefined

      const metadataIndicatorResponse = await fetchData(
        getEndpointFromConfig(endpointList, endpointLabels.Metadata)
          .apiEndpoint,
        'POST',
        indicatorRequest,
        headers
      )

      const metadataDatasetResponse = await fetchData(
        getEndpointFromConfig(endpointList, endpointLabels.Metadata)
          .apiEndpoint,
        'POST',
        datasetRequest,
        headers
      )

      if (
        metadataIndicatorResponse &&
        metadataIndicatorResponse['@odata.count'] != null
      ) {
        setIndicatorCount(metadataIndicatorResponse['@odata.count'])
      }
      if (
        metadataDatasetResponse &&
        metadataDatasetResponse['@odata.count'] != null
      ) {
        setDatasetCount(metadataDatasetResponse['@odata.count'])
      }
    } catch (error) {
      console.error('Error fetching metadata:', error)
    }
  }

  const handleIndicatorChange = useCallback(
    (renderedSubtopicIndex, subtopicId, indicatorIndex) => {
      const selectedIndicatorId = topicIndicators[indicatorIndex].id

      const tempAllIndicators = topicIndicators.map((indicator, i) => ({
        ...indicator,
        isSelected: i === indicatorIndex,
      }))
      setTopicIndicators(tempAllIndicators)

      const subTopicIndex = topicDetails.subTopics.findIndex((subTopic) =>
        subTopic.indicators.some(
          (indicator) => indicator.id === selectedIndicatorId
        )
      )

      let tempTopicdetails = clone(topicDetails)

      if (renderedSubtopicIndex !== subTopicIndex) {
        // Deselect previous subtopic indicators
        const renderedSubtopic =
          tempTopicdetails.subTopics[renderedSubtopicIndex]
        renderedSubtopic.indicators.forEach((indicator) => {
          indicator.isSelected = false
        })
        renderedSubtopic.isSelectedSubTopic = false

        // Select new subtopic and its indicators
        const newSubtopic = tempTopicdetails.subTopics[subTopicIndex]
        newSubtopic.isSelectedSubTopic = true
        newSubtopic.indicators.forEach((indicator) => {
          indicator.isSelected = indicator.id === selectedIndicatorId
        })

        setTopicDetails(tempTopicdetails)
      } else {
        // Update indicators for the same subtopic without looping twice
        const updatedIndicators = tempTopicdetails.subTopics[
          renderedSubtopicIndex
        ].indicators.map((indicator) => ({
          ...indicator,
          isSelected: indicator.id === selectedIndicatorId,
        }))

        tempTopicdetails.subTopics[renderedSubtopicIndex].indicators =
          updatedIndicators

        // Perform a single state update
        setTopicDetails(tempTopicdetails)
      }
    },
    [topicDetails, topicIndicators]
  )

  const handleSwipperClick = useCallback(
    (renderedSubtopicIndex, direction) => {
      const previousTopicIndicatorsIndex = topicIndicators.findIndex(
        (indicator) => indicator.isSelected
      )

      const selectedTopicIndicatorsIndex =
        direction === 'next'
          ? previousTopicIndicatorsIndex + 1
          : previousTopicIndicatorsIndex - 1

      if (
        selectedTopicIndicatorsIndex < 0 ||
        selectedTopicIndicatorsIndex >= topicIndicators.length
      ) {
        return
      }

      const selectedIndicatorId =
        topicIndicators[selectedTopicIndicatorsIndex].id

      // Update the selected state of topic indicators
      const updatedTopicIndicators = topicIndicators.map((indicator, i) => ({
        ...indicator,
        isSelected: i === selectedTopicIndicatorsIndex,
      }))
      setTopicIndicators(updatedTopicIndicators)

      const subTopicIndex = topicDetails.subTopics.findIndex((subTopic) =>
        subTopic.indicators.some(
          (indicator) => indicator.id === selectedIndicatorId
        )
      )

      const tempTopicDetails = clone(topicDetails)

      // Handle subtopic switch
      if (renderedSubtopicIndex !== subTopicIndex) {
        const previousSubtopic =
          tempTopicDetails.subTopics[renderedSubtopicIndex]
        previousSubtopic.isSelectedSubTopic = false
        previousSubtopic.indicators.forEach(
          (indicator) => (indicator.isSelected = false)
        )

        // const newSubtopic = tempTopicDetails.subTopics[subTopicIndex]
        tempTopicDetails.subTopics[subTopicIndex].isSelectedSubTopic = true
      }

      // Handle indicator switch within the same subtopic
      tempTopicDetails.subTopics[subTopicIndex].indicators.forEach(
        (indicator) => {
          indicator.isSelected = indicator.id === selectedIndicatorId
        }
      )

      const newIndex = tempTopicDetails.subTopics[
        subTopicIndex
      ].indicators.findIndex((obj) => obj.isSelected)
      const currentIndicators =
        tempTopicDetails.subTopics[subTopicIndex].indicators
      // let newIndex =
      //   direction === 'next'
      //     ? subTopicIndicatorsIndex + 1
      //     : subTopicIndicatorsIndex - 1

      if (newIndex < 0 || newIndex >= currentIndicators.length) return

      const updatedIndicators = currentIndicators.map((indicator, i) => ({
        ...indicator,
        isSelected: i === newIndex,
      }))

      setLoadingStates((prev) => ({
        ...prev,
        [topicIndicators[previousTopicIndicatorsIndex].id]: false,
        [currentIndicators[newIndex].id]: true,
      }))

      tempTopicDetails.subTopics[subTopicIndex].indicators = updatedIndicators

      setTopicDetails((prev) => ({
        ...prev,
        subTopics: tempTopicDetails.subTopics,
      }))
    },
    [topicDetails, topicIndicators]
  )

  const handleSubTopicChange = useCallback(
    (event, subTopicId) => {
      event?.preventDefault()

      const previousSubTopic = topicDetails.subTopics.find(
        (subTopic) => subTopic.isSelectedSubTopic
      )

      // Only proceed if there's a change in the selected sub-topic
      if (previousSubTopic?.id !== subTopicId) {
        const updatedSubTopics = topicDetails.subTopics.map((subTopicObj) => ({
          ...subTopicObj,
          indicators: subTopicObj.indicators.map((obj) => ({
            ...obj,
            isSelected: subTopicObj.id === subTopicId,
          })),
          isSelectedSubTopic: subTopicObj.id === subTopicId, // Mark the selected sub-topic
        }))

        const newSubTopic = updatedSubTopics.find(
          (subTopic) => subTopic.id === subTopicId
        )

        setTopicDetails((prev) => ({
          ...prev,
          subTopics: updatedSubTopics,
        }))

        const prevIndicatorId = previousSubTopic?.indicators[0]?.id
        const newIndicatorId = newSubTopic?.indicators[0]?.id

        if (prevIndicatorId !== undefined || newIndicatorId !== undefined) {
          setLoadingStates((prev) => ({
            ...prev,
            ...(prevIndicatorId !== undefined && { [prevIndicatorId]: false }),
            ...(newIndicatorId !== undefined && { [newIndicatorId]: true }),
          }))
        }

        const tempIndicators = topicIndicators.map((indicator) => ({
          ...indicator,
          isSelected: indicator.id === newIndicatorId,
        }))

        setTopicIndicators(tempIndicators)
      }
    },
    [topicDetails, topicIndicators]
  )

  const getSubTopicIndex = (indicator) => {
    const index = topicDetails.subTopics.findIndex((obj) =>
      // (obj) => obj.id === indicator.id
      obj.indicators.some((obj2) => obj2.isSelected)
    )

    return index
  }

  return (
    <div className="tui_data_card tui_data_card_lg aem-GridColumn--xs--12 aem-GridColumn--phone--12 aem-GridColumn--tablet--12 aem-GridColumn--desktop--6 aem-GridColumn--default--6">
      <div className="tui_card_wrapper">
        <div className="tui_card_content_right">
          <div className="tui_card_title">
            <img
              className="tui_card_icon"
              src={`${redirectURL}/img/topics/${topicDetails.technical_value}.svg`}
              alt={`${topicDetails.id} icon`}
            />
            <h3 className="tui_card_title_text">
              <a
                href={`${topicRedirectUrl}${topicDetails.technical_value}${
                  countryDropdownValue ? `?country=${countryDropdownValue}` : ''
                }${environment === 'author' ? '.html' : ''}`}
                data-customlink="nl:body content"
              >
                {topicDetails.value}
              </a>
            </h3>
          </div>
          <div className="tui_card_content_left">
            <ul className="tui_card_data">
              <li className="tui_card_data_item">
                <p className="tui_card_data_title">Indicators</p>
                <p className="tui_card_data_value">
                  <a
                    href={`${searchRedirectUrl}?tab=indicator&themeAndTopics=${topicDetails.id}`}
                    data-customlink="nl:body content"
                  >
                    {indicatorCount}
                  </a>
                </p>
              </li>
              <li className="tui_card_data_item">
                <p className="tui_card_data_title">Datasets</p>
                <p className="tui_card_data_value">
                  <a
                    href={`${searchRedirectUrl}?tab=dataset&themeAndTopics=${topicDetails.id}`}
                    data-customlink="nl:body content"
                  >
                    {datasetCount}
                  </a>
                </p>
              </li>
            </ul>
          </div>
        </div>
        <ul className="tui_card_btn_group">
          {topicDetails.subTopics?.map((obj) => (
            <li key={obj.id}>
              <button
                className={`tui_card_btn_item ${
                  obj.isSelectedSubTopic ? 'active' : ''
                }`}
                id={obj.id}
                onClick={(event) => handleSubTopicChange(event, obj.id)}
                data-customlink="nl:body content"
              >
                {obj.value}
              </button>
            </li>
          ))}
        </ul>
        {topicDetails.subTopics?.map((obj, index) => (
          <div
            key={`subTopic-${obj.id}`}
            className="swiper tui_innerSwiper swiper-initialized swiper-horizontal swiper-autoheight"
            style={{ display: `${!obj.isSelectedSubTopic ? 'none' : ''}` }}
          >
            {obj.isSelectedSubTopic ? (
              <IndicatorDetails
                subTopic={obj}
                key={`${obj.id}-${index}`}
                setLoadingStates={setLoadingStates}
                loadingStates={loadingStates}
                subTopicIndex={index}
                indicatorRedirectUrl={indicatorRedirectUrl}
                featuredDatasetsData={featuredDatasetsData}
              />
            ) : (
              <div
                className="swiper-wrapper"
                id="swiper-wrapper-a82b45bb8cda9f10f"
                aria-live="polite"
              ></div>
            )}
            {topicIndicators?.length > 1
              ? (() => {
                  const subtopicIndex = getSubTopicIndex(obj)

                  return (
                    <div className="swiper-controller">
                      <div
                        className="swiper-button-prev"
                        role="button"
                        aria-label="Previous slide"
                        aria-controls="swiper-wrapper-a82b45bb8cda9f10f"
                        aria-disabled="true"
                        aria-hidden="true"
                        onClick={() =>
                          handleSwipperClick(subtopicIndex, 'previous')
                        }
                        data-customlink="nl:body content"
                      ></div>
                      <div
                        className="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal"
                        aria-hidden="true"
                        tabIndex="-1"
                      >
                        {topicIndicators?.map((obj, index) => (
                          <span
                            key={`${obj.id}-pagination-${index}`}
                            className={`swiper-pagination-bullet ${
                              obj.isSelected
                                ? 'swiper-pagination-bullet-active'
                                : ''
                            }`}
                            role="button"
                            aria-label={obj.id}
                            aria-current="true"
                            onClick={() =>
                              handleIndicatorChange(
                                subtopicIndex,
                                topicDetails.subTopics[subtopicIndex].id,
                                index
                              )
                            }
                          ></span>
                        ))}
                      </div>
                      <div
                        className="swiper-button-next"
                        role="button"
                        aria-label="Next slide"
                        aria-controls="swiper-wrapper-a82b45bb8cda9f10f"
                        aria-disabled="false"
                        aria-hidden="true"
                        onClick={() =>
                          handleSwipperClick(subtopicIndex, 'next')
                        }
                        data-customlink="nl:body content"
                      ></div>
                    </div>
                  )
                })()
              : null}
          </div>
        ))}
        <div className="tui_Card_link_group">
          <a
            className="tui_card_btn_item"
            href={`${topicRedirectUrl}${topicDetails.technical_value}${
              environment === 'author' ? '.html' : ''
            }`}
            key={`${topicDetails.technical_value}`}
            data-customlink="nl:body content"
          >
            Visit Topic Page <i className="lp lp-chevron-right"></i>
          </a>
        </div>
      </div>
    </div>
  )
}

export default CardDetails
